<template>
  <div
    v-if="doc"
    v-click-outside="hideSettings"
    :class="doc.controls.readingSettingsClass"
    title="Reading settings"
    @click="doc.controls.toggleReadingSettings($event)"
  >
    <div class="control__icon-box">
      <span class="control__icon iconfont icontrol-font"></span>
    </div>
    <transition name="fade">
      <div v-if="doc.controls.readingSettingsVisible" class="control-font">
        <div class="control-font__unit">
          <h3 class="control-font__title">Font Size</h3>
          <div class="control-font__check">
            <button
              v-for="(item, index) in doc.settings.readingOptions.fontOptions"
              :key="item.title"
              :class="doc.settings.getFontSizeClass(item, index)"
              :title="item.title"
              @click="setFontSize(item.size)"
            >
              <div class="control-font__size-icon-box">
                <span class="control-font__size-icon icontrol-font"></span>
              </div>
            </button>
          </div>
        </div>
        <div class="control-font__unit">
          <h3 class="control-font__title">Font Family</h3>
          <div
            class="control-font__select select-box"
            @click="showFonts = !showFonts"
          >
            <span
              :class="{ 'control-font__menu-icon-active': showFonts }"
              class="control-font__menu-icon iconfont isub-menu"
            ></span>
            <input
              class="control-font__family-name select"
              type="text"
              onfocus="this.blur()"
              :style="{ 'font-family': selectedFontFamily.font }"
              :placeholder="selectedFontFamily.title"
            />
            <ul
              v-if="showFonts"
              class="control-font__option option"
              style="display: block"
            >
              <li
                v-for="item in doc.settings.readingOptions.fontFamilyOptions"
                :key="item.title"
                class="control-font__option-c"
                @click="setFontFamily(item)"
              >
                <button class="reading-settings control-font__family-link">
                  <span
                    class="select-c"
                    :style="{ 'font-family': item.font }"
                    >{{ item.title }}</span
                  >
                </button>
              </li>
            </ul>
          </div>
        </div>
        <div class="control-font__unit">
          <h3 class="control-font__title">Line Height</h3>
          <div class="control-font__check">
            <button
              v-for="(item, index) in doc.settings.readingOptions.lineOptions"
              :key="item.title"
              :class="doc.settings.getLineHeightClass(item, index)"
              data-setting="item.size"
              :title="item.title"
              @click="setLineHeight(item.size)"
            >
              <span class="control-font__line-height-icon"></span>
              <span class="control-font__line-height-icon"></span>
              <span class="control-font__line-height-icon"></span>
            </button>
          </div>
        </div>
        <div class="control-font__unit">
          <h3 class="control-font__title">Margins</h3>
          <div class="control-font__check">
            <button
              v-for="(item, index) in doc.settings.readingOptions.marginOptions"
              :key="item.title"
              :class="doc.settings.getMarginClass(item, index)"
              data-setting="item.size"
              :title="item.title"
              @click="setMargins(item.size)"
            >
              <span class="control-font__margins-icon"></span>
              <span class="control-font__margins-icon"></span>
              <span class="control-font__margins-icon"></span>
            </button>
          </div>
        </div>
        <div class="control-font__btn-box control__font__unit">
          <a
            class="control-font__btn btns btns--white btns--full btns--small reading-settings"
            @click="resetToDefaults()"
            >Reset to Defaults</a
          >
        </div>
      </div>
    </transition>
  </div>
</template>

<script setup lang="ts">
/* eslint-disable vue/no-mutating-props */
import { onBeforeMount, ref } from 'vue'

import { DocView } from '@/models/doc'
import {
  FontFamilyOption,
  FONT_FAMILY_OPTIONS,
  FontSize,
  LineHeight,
  MarginSize,
} from '@/models/doc.settings'

const props = defineProps<{
  doc: DocView
}>()
const showFonts = ref(false)
const selectedFontFamily = ref<FontFamilyOption>(FONT_FAMILY_OPTIONS[0])

onBeforeMount(() => {
  selectedFontFamily.value = props.doc.settings.selectedFontFamily
})

function hideSettings(): void {
  props.doc.controls.hideReadingSettings()
}

function setFontSize(fontSize: FontSize): void {
  props.doc.settings.fontSize = fontSize
  props.doc.settings.saveSettings()
}

function setMargins(marginSize: MarginSize): void {
  props.doc.settings.marginSize = marginSize
  props.doc.settings.saveSettings()
}

function setLineHeight(lineHeight: LineHeight): void {
  props.doc.settings.lineHeight = lineHeight
  props.doc.settings.saveSettings()
}

function setFontFamily(fontFamily: FontFamilyOption): void {
  props.doc.settings.fontFamily = fontFamily.font
  selectedFontFamily.value = fontFamily
  props.doc.settings.saveSettings()
}

function resetToDefaults(): void {
  props.doc.settings.resetToDefaults()
  selectedFontFamily.value = props.doc.settings.selectedFontFamily
}
</script>
